import { useI18n } from '@ecom/i18n';
import { Button, LanguageSwitcher, useTheme } from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLocale } from '@/lib/locale';
import { useSession } from '@/lib/session';

/**
 * No search palette, no shop switcher (merchant-dashboard's own Topbar
 * carries both) — this app has two real screens and no shop concept at
 * all. Theme toggle + sign-out only.
 */
export function Topbar(): ReactElement {
  // Not destructured — `themeState.toggleTheme` rather than a bare
  // `toggleTheme`, the identical avoidance merchant-dashboard's own
  // Topbar/CommandPalette already note (a destructured method trips
  // `@typescript-eslint/unbound-method`).
  const themeState = useTheme();
  const { clearSession } = useSession();
  const { locale, setLocale } = useLocale();
  const { t } = useI18n();
  const navigate = useNavigate();

  return (
    <header className="flex flex-wrap items-center justify-between gap-y-2 border-b border-line bg-surface px-5 py-3">
      <span className="text-sm font-medium text-inkFaint">{t('admin.topbar.label')}</span>

      <div className="flex flex-wrap items-center gap-2">
        <LanguageSwitcher
          locale={locale}
          onChange={(next) => {
            setLocale(next);
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            themeState.toggleTheme();
          }}
          aria-label={t('merchant.topbar.themeToggleAria')}
        >
          {themeState.theme === 'dark'
            ? t('merchant.topbar.lightMode')
            : t('merchant.topbar.darkMode')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            clearSession();
            void navigate('/login', { replace: true });
          }}
        >
          {t('merchant.topbar.logout')}
        </Button>
      </div>
    </header>
  );
}
